import { groupBy } from 'fp-ts/NonEmptyArray';
import { FC, useCallback, useMemo } from 'react';
import { Product, ProductCategory } from '@mews-commander/data-access';
import { SelectField, SelectFieldProps } from '@mews-commander/form';
import { GroupOption, Option } from '@mews-commander/types';
import { LocalizedProperty, LocalizedPropertyProps, lt, useLocalizeProperty } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';

const NO_CATEGORY_ID = 'null';

const groupProductByCategoryId = groupBy<Product>((p) => p.productCategoryId ?? NO_CATEGORY_ID);

interface ProductSelectFieldProps extends Omit<SelectFieldProps, 'options'> {
    products: Product[];
    productCategories?: ProductCategory[] | null;
}

export const ProductSelectField: FC<ProductSelectFieldProps> = ({ products, productCategories, ...other }) => {
    const lp = useLocalizeProperty();

    const options = useMemo((): GroupOption[] | Option[] => {
        if (!productCategories?.length) {
            return products.map(productToOption);
        }

        const productsByCategoryId = groupProductByCategoryId(products);

        const productOptionsWithCategory: GroupOption[] = productCategories.reduce((acc, category) => {
            if (!productsByCategoryId[category.id]?.length) {
                return acc;
            }
            return [
                ...acc,
                {
                    groupLabel: <LocalizedProperty property={category.names} />,
                    groupOptions: productsByCategoryId[category.id]?.map(productToOption) ?? [],
                },
            ];
        }, [] as GroupOption[]);

        if (productsByCategoryId[NO_CATEGORY_ID]?.length) {
            productOptionsWithCategory.push({
                groupLabel: productOptionsWithCategory.length ? lt(TextKey.Other) : null,
                groupOptions: productsByCategoryId[NO_CATEGORY_ID].map(productToOption),
            });
        }
        return productOptionsWithCategory;
    }, [productCategories, products]);

    const searchProducts: SelectFieldProps['customSearch'] = useCallback(
        (component: React.ReactElement, searchValue: string): boolean => {
            const label = component.props.label as React.ReactElement<LocalizedPropertyProps>;

            return lp(label?.props.property)
                .toLowerCase()
                .includes(searchValue);
        },
        [lp]
    );

    return <SelectField options={options} {...other} customSearch={searchProducts} />;
};

const productToOption = (product: Product): Option => ({
    value: product.id,
    label: <LocalizedProperty property={product.names} />,
    disabled: false,
    inactive: false,
    indent: false,
    secondaryText: null,
});
